import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { THEME_COLORS } from "../../../ThemeConfig";
import plusIcon from "../../../assets/icons/rounded-plus.svg";
import playIcon from "../../../assets/icons/rounded-play-button.svg";
import profileIcon from "../../../assets/icons/rounded-profile-icon.png";

import TabbedSelector from "../../includes/homescreens/TabbedSelector";
import SessionDetailedCard from "../../includes/homescreens/SessionDetailedCard";
import BottomNavigation from "../../general/BottomNavigation";
import VideoPlayer from "../../includes/chip/VideoPlayer";
import EmbraceCard from "../../includes/homescreens/EmbraceCard";
import NoSessionCard from "../../includes/homescreens/NoSessionCard";

import { Context } from "../../../contexts/Store";
import { PatientConfig } from "../../../axiosConfig";
import Lottie from "lottie-react";
import LoaderLottie from "../../../assets/lottie/LoaderLottie.json";
import PaymentStatusModal from "../../includes/modals/PaymentStatusModal";
import UserValidationModal from "../../includes/modals/UserValidationModal";
import useUserValidation from "../../../hooks/useUserValidation";

const tabOptions = [
  { id: 1, name: "Upcoming", value: "upcoming" },
  { id: 2, name: "Completed", value: "completed" },
  { id: 3, name: "Cancelled", value: "cancelled" },
  { id: 4, name: "Pending", value: "pending" },
  { id: 5, name: "Missed/Failed", value: "failed" },
];

export default function DashboardPage({
  isExistingSession,
  isASessionAccepted,
  setIsASessionAccepted,
}) {
  const [menu, setMenu] = useState("sessions");
  const { paymentId } = useParams();
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get("payment_status");
  const [VideoDuration, setVideoDuration] = useState(0);
  const [sessionDetails, setSessionDetails] = useState([]);
  const [allConcerns, setAllConcerns] = useState([]);
  const [commonConcerns, setCommonConcerns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [videoContents, setVideoContents] = useState([]);
  const [displayCount, setDisplayCount] = useState(4);
  const [isPaymentDataLoading, setIsPaymentDataLoading] = useState(false);
  const [isPaymentModal, setIsPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({});
  const [paymentDataFetched, setPaymentDataFetched] = useState(false);

  const { state, dispatch } = useContext(Context);
  const accessToken = state.user_details?.access_token;
  const auth = state.user_details?.is_verified;
  const isPaymentPending = state.user_details?.ipp;
  const navigate = useNavigate();

  const { isUserActive, validationError } = useUserValidation({
    checkInterval: 30000,
    enabled: auth && state.user_details?.is_login,
  });

  const [selectedTab, setSelectedTab] = useState(tabOptions[0].value);
  const limitedCommonConcerns = commonConcerns.slice(0, displayCount);

  useEffect(() => {
    getVideo();
  }, []);

  useEffect(() => {
    getSessionDetails();
  }, [selectedTab]);

  useEffect(() => {
    getAllConcerns();
    getCommonConcerns();
  }, []);

  useEffect(() => {
    if (selectedTab === "upcoming" && isASessionAccepted) {
      getSessionDetails();
      if (setIsASessionAccepted) setIsASessionAccepted(false);
    }
  }, [selectedTab, isASessionAccepted]);

  useEffect(() => {
    getTimeBasedGreeting();
  }, []);

  useEffect(() => {
    if (sessionDetails.length === 1) setDisplayCount(2);
    else if (sessionDetails.length === 2) setDisplayCount(3);
    else if (sessionDetails.length >= 3) setDisplayCount(4);
    else setDisplayCount(2);
  }, [sessionDetails]);

  useEffect(() => {
    if (paymentStatus === "success" && paymentId && !paymentDataFetched) {
      setIsPaymentDataLoading(true);
      getPaymentData();
    } else if (paymentId && isPaymentPending && !paymentDataFetched) {
      getPaymentData();
    }
    if (
      paymentId &&
      (isPaymentPending || paymentStatus === "success") &&
      paymentDataFetched &&
      !isPaymentDataLoading
    ) {
      setIsPaymentModal(true);
    }
  }, [paymentId, isPaymentPending, paymentStatus, isPaymentDataLoading, paymentDataFetched]);

  function getVideo() {
    PatientConfig.get("api/v1/general/videos?purpose=2")
      .then((res) => setVideoContents(res.data.data || []))
      .catch(() => {});
  }

  function getSessionDetails() {
    setIsLoading(true);
    PatientConfig.get(`api/v1/cases/sessions?type=${selectedTab}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => {
        setSessionDetails(res.data?.data?.data || []);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }

  function getAllConcerns() {
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    PatientConfig.get("api/v1/experts/area-of-expertises/", { headers })
      .then((res) => {
        const data = (res.data?.data || []).filter((item) => item.name !== "Other");
        setAllConcerns(data);
      })
      .catch(() => {});
  }

  function getCommonConcerns() {
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    PatientConfig.get("api/v1/experts/area-of-expertises/?is_common_concern=true", { headers })
      .then((res) => {
        const data = (res.data?.data || []).filter((item) => item.name !== "Other");
        setCommonConcerns(data);
      })
      .catch(() => {});
  }

  function getPaymentData() {
    setIsPaymentDataLoading(true);
    PatientConfig.get(`/api/v1/payments/razorpay-payment-data/${paymentId}/`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => {
        const { StatusCode, data } = res.data;
        if (StatusCode === 6000) {
          setPaymentData(data);
          setPaymentDataFetched(true);
        }
        setIsPaymentDataLoading(false);
      })
      .catch(() => setIsPaymentDataLoading(false));
  }

  function getTimeBasedGreeting() {
    const hrs = new Date().getHours();
    if (hrs < 12) setWelcomeMessage("Good Morning");
    else if (hrs < 16) setWelcomeMessage("Good Afternoon");
    else setWelcomeMessage("Good Evening");
  }

  function handleConcernClick(item) {
    if (auth) {
      dispatch({ type: "ADD_SESSION", new_session: { concern: item.id } });
      navigate("/create-session");
    } else {
      navigate("/?auth=login&next=/create-session");
    }
  }

  return (
    <>
      <PageWrap>
        <Container>
          {/* Hero-style welcome */}
          <HeroSection>
            <HeroTag>Your Companion to Wellness</HeroTag>
            <HeroTitle>
              {welcomeMessage},{" "}
              <HeroName>{state.user_details?.user_name || ""}</HeroName>
            </HeroTitle>
          </HeroSection>

          {/* Quick CTA - Book a Session */}
          <CtaSection>
            <CtaCard>
              <CtaCardText>Ready for your next session?</CtaCardText>
              <CtaButton to="/create-session">Book a Session</CtaButton>
            </CtaCard>
          </CtaSection>

          <TwoCol>
            {/* Main content */}
            <MainCol>
              {auth && isExistingSession && (
                <TabRow>
                  <MenuPill
                    active={menu === "sessions"}
                    onClick={() => setMenu("sessions")}
                  >
                    My Sessions
                  </MenuPill>
                  <MenuPill
                    active={menu === "get-started"}
                    onClick={() => setMenu("get-started")}
                  >
                    {isExistingSession ? "Concerns" : "Get Started"}
                  </MenuPill>
                </TabRow>
              )}

              {(!isExistingSession || menu === "get-started") && (
                <Section>
                  <SectionLabel>Explore by concern</SectionLabel>
                  <SectionTag>Concerns</SectionTag>
                  <SectionDesc>
                    Choose a concern to start your journey.
                  </SectionDesc>
                  <CardsGrid>
                    {allConcerns.map((concern) => (
                      <SessionCard key={concern.id} onClick={() => handleConcernClick(concern)}>
                        <SessionCardAccent />
                        <SessionCardBody>
                          <SessionCardTitle>{concern.name}</SessionCardTitle>
                          <SessionCardSubtitle>{concern.title || ""}</SessionCardSubtitle>
                          <SessionCardDesc>{concern.description || ""}</SessionCardDesc>
                          <SessionCardActions>
                            <LearnMoreLink to={`/concerns/${concern.id}`} onClick={(e) => e.stopPropagation()}>
                              Learn more →
                            </LearnMoreLink>
                            <BookNowBtn onClick={(e) => { e.stopPropagation(); handleConcernClick(concern); }}>
                              Book now
                            </BookNowBtn>
                          </SessionCardActions>
                        </SessionCardBody>
                      </SessionCard>
                    ))}
                  </CardsGrid>
                </Section>
              )}

              {isExistingSession && menu === "sessions" && (
                <Section>
                  <SectionLabel>Your sessions</SectionLabel>
                  <SectionTag>My Sessions</SectionTag>
                  <TabbedSelector
                    tabOptions={tabOptions}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                  />
                  <SessionsCard>
                    {isLoading ? (
                      <LoaderWrap>
                        <Lottie animationData={LoaderLottie} style={{ width: 80, height: 80 }} />
                      </LoaderWrap>
                    ) : sessionDetails.length > 0 ? (
                      sessionDetails.map((sessionDetail) => (
                        <SessionDetailedCard
                          key={sessionDetail?.id}
                          selectedTab={selectedTab}
                          menu={menu}
                          setMenu={setMenu}
                          sessionDetail={sessionDetail}
                          getSessionDetails={getSessionDetails}
                        />
                      ))
                    ) : (
                      <NoSessionCard selectedTab={selectedTab} />
                    )}
                  </SessionsCard>
                </Section>
              )}
            </MainCol>

            {/* Sidebar - same as landing feel */}
            <SideCol>
              <SideCard>
                <EmbraceCard />
              </SideCard>
              {isExistingSession && (
                <SideCard>
                  <SideCardTitle>Common Concerns</SideCardTitle>
                  <SideSessionList>
                    {limitedCommonConcerns.map((concern) => (
                      <SideSessionItem key={concern.id}>
                        <SideSessionAccent />
                        <SideSessionContent>
                          <SideSessionTitle>{concern.name}</SideSessionTitle>
                          {concern.title ? <SideSessionSubtitle>{concern.title}</SideSessionSubtitle> : null}
                          {concern.description ? (
                            <SideSessionDesc>{concern.description}</SideSessionDesc>
                          ) : null}
                        </SideSessionContent>
                        <SideSessionActions>
                          <SideLearnMoreLink to={`/concerns/${concern.id}`}>Learn more →</SideLearnMoreLink>
                          <BookNowBtnSmall onClick={(e) => { e.preventDefault(); handleConcernClick(concern); }}>
                            Book now
                          </BookNowBtnSmall>
                        </SideSessionActions>
                      </SideSessionItem>
                    ))}
                  </SideSessionList>
                  {commonConcerns.length > displayCount && (
                    <LoadMoreBtn
                      onClick={() =>
                        setDisplayCount(
                          displayCount >= commonConcerns.length
                            ? 2
                            : commonConcerns.length
                        )
                      }
                    >
                      {displayCount >= commonConcerns.length ? "View Less" : "Load More"}
                    </LoadMoreBtn>
                  )}
                </SideCard>
              )}
              <SideCard>
                <VideoPlayer
                  item={videoContents}
                  setVideoDuration={setVideoDuration}
                  Icon={playIcon}
                />
                <VideoMeta>
                  <ProfilePic src={profileIcon} alt="" />
                  <VideoMetaText>
                    <VideoTitle>{videoContents?.title || "Video"}</VideoTitle>
                    <VideoTime>{VideoDuration}</VideoTime>
                  </VideoMetaText>
                </VideoMeta>
              </SideCard>
            </SideCol>
          </TwoCol>
        </Container>
      </PageWrap>

      <BottomNavigation />

      {((paymentStatus === "success" && isPaymentDataLoading) ||
        (paymentId && isPaymentPending && isPaymentDataLoading)) && (
        <PaymentLoadingOverlay>
          <LoadingContent>
            <Lottie animationData={LoaderLottie} style={{ width: 80, height: 80 }} loop />
            <LoadingText>Processing your payment...</LoadingText>
          </LoadingContent>
        </PaymentLoadingOverlay>
      )}

      {isPaymentModal && (
        <PaymentStatusModal
          isPaymentModal={isPaymentModal}
          setIsPaymentModal={setIsPaymentModal}
          paymentData={paymentData}
        />
      )}

      <UserValidationModal
        isOpen={!isUserActive && auth && state.user_details?.is_login}
        message={validationError || "Your account has been deactivated or deleted by admin."}
      />
    </>
  );
}

const PageWrap = styled.div`
  min-height: calc(100vh - 70px);
  background: ${THEME_COLORS.page_bg};
  padding: 80px 0 32px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 80px 16px 100px;
  }
`;
const Container = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;
const HeroSection = styled.section`
  margin-bottom: 20px;
  padding: 20px 24px;
  background: ${THEME_COLORS.hero_gradient};
  border-radius: 12px;
  box-shadow: ${THEME_COLORS.elevation_user_u4};
  border: 1px solid ${THEME_COLORS.accent_teal_soft};
  @media (max-width: 768px) {
    padding: 16px 20px;
    margin-bottom: 16px;
  }
`;
const HeroTag = styled.p`
  font-family: "DM_sans";
  font-size: 13px;
  color: ${THEME_COLORS.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 4px 0;
`;
const HeroTitle = styled.h1`
  font-family: "DM_sans_medium";
  font-size: 24px;
  color: ${THEME_COLORS.text_title};
  line-height: 1.3;
  margin: 0;
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;
const HeroName = styled.span`
  color: ${THEME_COLORS.primary};
`;
const CtaSection = styled.div`
  margin-bottom: 24px;
`;
const CtaCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, ${THEME_COLORS.chips_green_container} 0%, ${THEME_COLORS.surface_soft} 100%);
  border-radius: 12px;
  box-shadow: ${THEME_COLORS.elevation_user_u4};
  border: 1px solid ${THEME_COLORS.accent_teal_soft};
`;
const CtaCardText = styled.p`
  font-family: "DM_sans_medium";
  font-size: 18px;
  color: ${THEME_COLORS.text_title};
  margin: 0;
`;
const CtaButton = styled(Link)`
  padding: 12px 28px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 15px;
  border-radius: 32px;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  align-items: start;
  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;
const MainCol = styled.div`
  min-width: 0;
`;
const TabRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;
const MenuPill = styled.button`
  padding: 10px 20px;
  font-family: "DM_sans_medium";
  font-size: 15px;
  border-radius: 32px;
  border: 2px solid ${THEME_COLORS.light_400};
  background: ${(p) => (p.active ? THEME_COLORS.white : THEME_COLORS.light_200)};
  color: ${(p) => (p.active ? THEME_COLORS.primary : THEME_COLORS.text_para)};
  cursor: pointer;
  box-shadow: ${(p) => (p.active ? THEME_COLORS.elevation_user_u4 : "none")};
  transition: all 0.2s;
  &:hover {
    border-color: ${THEME_COLORS.primary};
    color: ${THEME_COLORS.primary};
  }
`;
const Section = styled.section`
  margin-bottom: 24px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;
const SectionLabel = styled.p`
  font-family: "DM_sans";
  font-size: 12px;
  color: ${THEME_COLORS.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 6px 0;
`;
const SectionTag = styled.h2`
  font-family: "DM_sans_medium";
  font-size: 20px;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 6px 0;
`;
const SectionDesc = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.text_para};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
`;
const SessionCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${THEME_COLORS.white};
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid ${THEME_COLORS.light_300};
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.2s ease;
  overflow: hidden;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
    border-color: ${THEME_COLORS.chips_green_container};
  }
`;
const SessionCardAccent = styled.div`
  height: 4px;
  background: ${THEME_COLORS.chips_green_on_container};
`;
const SessionCardBody = styled.div`
  padding: 24px 24px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const SessionCardTitle = styled.h3`
  font-family: "DM_sans_medium";
  font-size: 20px;
  font-weight: 600;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 8px 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;
const SessionCardSubtitle = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
  margin: 0 0 12px 0;
  line-height: 1.4;
`;
const SessionCardDesc = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.text_para};
  line-height: 1.55;
  margin: 0 0 20px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  min-height: 3.9em;
`;
const SessionCardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid ${THEME_COLORS.divider};
`;
const LearnMoreLink = styled(Link)`
  font-family: "DM_sans_medium";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const BookNowBtn = styled.button`
  padding: 10px 22px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 14px;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease;
  box-shadow: 0 2px 8px rgba(29, 166, 135, 0.25);
  &:hover {
    opacity: 0.95;
    transform: scale(1.02);
  }
`;
const PlusWrap = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;
const PlusIcon = styled.img`
  width: 24px;
  height: 24px;
`;
const SessionsCard = styled.div`
  background: ${THEME_COLORS.card_bg};
  border-radius: 16px;
  padding: 20px;
  box-shadow: ${THEME_COLORS.elevation_user_u4};
  border: 1px solid ${THEME_COLORS.light_300};
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;
const SideCol = styled.aside`
  @media (max-width: 1080px) {
    display: none;
  }
`;
const SideCard = styled.div`
  background: ${THEME_COLORS.card_bg};
  border-radius: 16px;
  padding: 20px;
  box-shadow: ${THEME_COLORS.elevation_user_u4};
  border: 1px solid ${THEME_COLORS.light_300};
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;
const SideCardTitle = styled.h3`
  font-family: "DM_sans_medium";
  font-size: 16px;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 12px 0;
`;
const SideSessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const SideSessionItem = styled.div`
  display: flex;
  flex-direction: column;
  background: ${THEME_COLORS.card_bg};
  border-radius: 12px;
  padding: 0;
  border: 1px solid ${THEME_COLORS.light_300};
  box-shadow: ${THEME_COLORS.elevation_user_u4};
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  &:hover {
    box-shadow: ${THEME_COLORS.elevation_user_u3};
    border-color: ${THEME_COLORS.primary};
  }
`;
const SideSessionAccent = styled.div`
  height: 3px;
  background: ${THEME_COLORS.chips_green_on_container};
`;
const SideSessionContent = styled.div`
  padding: 12px 14px 10px;
`;
const SideSessionTitle = styled.span`
  display: block;
  font-family: "DM_sans_medium";
  font-size: 14px;
  font-weight: 600;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 4px 0;
  line-height: 1.3;
`;
const SideSessionSubtitle = styled.span`
  display: block;
  font-family: "DM_sans";
  font-size: 12px;
  color: ${THEME_COLORS.primary};
  margin: 0 0 8px 0;
  line-height: 1.35;
`;
const SideSessionDesc = styled.p`
  font-family: "DM_sans";
  font-size: 12px;
  color: ${THEME_COLORS.text_para};
  line-height: 1.35;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const SideSessionActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid ${THEME_COLORS.divider};
  background: ${THEME_COLORS.light_100};
`;
const SideLearnMoreLink = styled(Link)`
  font-family: "DM_sans_medium";
  font-size: 12px;
  color: ${THEME_COLORS.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const BookNowBtnSmall = styled.button`
  padding: 6px 14px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 12px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.95;
  }
`;
const PlusIconSmall = styled.img`
  width: 20px;
  height: 20px;
`;
const LoadMoreBtn = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  font-family: "DM_sans_medium";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
  background: none;
  border: 2px solid ${THEME_COLORS.light_400};
  border-radius: 32px;
  cursor: pointer;
  &:hover {
    border-color: ${THEME_COLORS.primary};
    background: ${THEME_COLORS.chips_green_container};
  }
`;
const VideoMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;
const ProfilePic = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;
const VideoMetaText = styled.div``;
const VideoTitle = styled.p`
  font-family: "DM_sans_medium";
  font-size: 14px;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 4px 0;
`;
const VideoTime = styled.p`
  font-family: "DM_sans";
  font-size: 12px;
  color: ${THEME_COLORS.text_para};
  margin: 0;
`;
const PaymentLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;
const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  background: ${THEME_COLORS.white};
  padding: 40px;
  border-radius: 16px;
  box-shadow: ${THEME_COLORS.elevation_user_u2};
`;
const LoadingText = styled.p`
  font-family: "DM_sans_medium";
  font-size: 16px;
  color: ${THEME_COLORS.text_title};
  margin: 0;
`;
